// src/com/user_info/ResetPasswordFirst.js
import React, { useEffect, useState } from 'react';
import { Box, fontWeight } from '@mui/system';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { TextField, Button } from '@mui/material';
import { AppPalette } from '../../model/AppConst';
import { INIT_USER_INFO_PASSWORD_FOR_AWS, UserInfoDictionary } from './UserInfoGql';
import ValueUtil from '../../model/ValueUtil';
import { useMutation, useReactiveVar } from '@apollo/client';
import { userInfoRepo } from '../../model/CvoModel';
import Util from '../../model/Util';
import ResponseAlert from '../message/ResponseAlert';

const ColumnBoxWidth = 500;
const ColumnMargin = 5;
//const ColumnCount = 2;
//const EditorWidth = ColumnBoxWidth * ColumnCount + ColumnMargin * ColumnCount * 2 + 20;

const InputContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '100%',
    marginTop: 20,
});
const EditorContentRow = styled(Box)({
    padding: 0,
    border: '1px solid ' + AppPalette.PrimaryRGB,
    borderRadius: 10,
});

const InputColumn = styled(Box)({
    display: 'inline-block',
    width: ColumnBoxWidth, // adjust here.
    paddingTop: 10,
    margin: ColumnMargin,
    verticalAlign: 'top',
    padding: 10,
});

const FieldBox = styled(Box)({
    display: 'block',
    paddingTop: 10,
});

const InputMap = UserInfoDictionary.awsInitMap; // 입력양식 상수, validation
const defaultInputData = {password:'', newPassword:'', email:null}; // 입력양식 상수 중 기초 데이터(default)만 추출.

const ErrorTitle =UserInfoDictionary.errorTitle;

export default function ResetPasswordFirst({
    onClose,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [changed, setChanged] = useState(false); // 입력으로 인한 변경이 있는가?
    //const [inputData, setInputData] = useState(item ? item : {...defaultInputData}); // 입력한 데이터.
    const [inputData, setInputData] = useState(defaultInputData); // 입력한 데이터.
    const [inputError, setInputError] = useState({}); // 입력된 것들 중 오류 여부 표시.
    const [hasError, setHasError] = useState(false); // 하나라도 오류가 있는가? 입력되지 않은 것이 있는가?
    // const {loading:loadingSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.
    const [responseAlert, setResponseAlert] = useState(null);
    const [changePassword, resChangePassword] = useMutation( INIT_USER_INFO_PASSWORD_FOR_AWS, {
        onCompleted: (data, option) => onCompleteChangePassword(data, option),
        onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.ChPwd})}
    } );

    useEffect(()=>{
        if(sessionInfo) {
            setInputData({...defaultInputData, email: sessionInfo.user.email || ''});
        }
    }, [sessionInfo]);
    
    const onCompleteChangePassword = (data, option) => {
        if(data.userInfoInitForAws.ok) {
            Util.bubbleSnack("암호가 변경되었습니다. 새 암호를 잊지 마세요~!");
            onClose(); // 닫기.
        }
        else setResponseAlert({open:true, resultData: data.userInfoInitForAws, title: ErrorTitle.ChPwd});
    };

    // 저장 호출은 컨트롤러로 보내고, 그 결과를 responseSaving 받아서 보여준다.
    const onClickSubmit = () => {
        if(inputData.newPassword === inputData.confirm) {
            const param = {
                password: inputData.password,
                newPassword: inputData.newPassword,
                email: inputData.email
            };

            // onClickMutate(param);
            changePassword({variables: {userInfo: param}});
        }
    };
    
    const onChangeTextData = (event) => {
        // evaluate input data against readiness.
        const id = event.target.id;
        let [newData, newError, hasError] = ValueUtil.evalTextInput(event, InputMap, inputData, inputError);
        
        if(id === InputMap.confirm.id && newData[id] !== newData.newPassword) {
            newError[id] = true; //"새 암호와 암호확인이 서로 다릅니다";
            hasError = true;
        }
        setInputData(newData);
        setInputError(newError);
        setHasError(hasError);
        setChanged(true);
    };

    return (
        <InputContainer>
            <EditorContentRow>
                <Box style={{
                    fontSize: '1.1rem', fontWeight: fontWeight.bold, padding: 15,
                    backgroundColor: AppPalette.PrimaryRGB, color: 'white',
                    textAlign: 'center', borderRadius: 5,
                }}>
                    임의 설정된 암호를 변경합니다.
                </Box>
                <InputColumn>
                    <form>
                    <FieldBox>
                        <TextField id={InputMap.password.id}
                            type={InputMap.password.type}
                            label={InputMap.password.label}
                            required={InputMap.password.required}
                            value={inputData.password || ''}
                            error={inputError.password}
                            helperText={InputMap.password.help}
                            fullWidth
                            onChange={onChangeTextData} />
                    </FieldBox>

                    <FieldBox>
                        <TextField id={InputMap.newPassword.id}
                            type={InputMap.newPassword.type}
                            label={InputMap.newPassword.label}
                            required={InputMap.newPassword.required}
                            value={inputData.newPassword || ''}
                            error={inputError.newPassword}
                            helperText={InputMap.newPassword.help}
                            fullWidth
                            onChange={onChangeTextData} />
                    </FieldBox>

                    <FieldBox>
                        <TextField id={InputMap.confirm.id}
                            type={InputMap.confirm.type}
                            label={InputMap.confirm.label}
                            required={InputMap.confirm.required}
                            value={inputData.confirm || ''}
                            error={inputError.confirm}
                            helperText={inputError.confirm ? "새 암호와 암호확인이 서로 다릅니다" : undefined}
                            fullWidth
                            onChange={onChangeTextData} />
                    </FieldBox>

                    <FieldBox>
                        <TextField id={InputMap.email.id}
                            type={InputMap.email.type}
                            label={InputMap.email.label + " (대소문자 주의)"}
                            required={InputMap.email.required}
                            value={inputData.email || ''}
                            error={inputError.email}
                            fullWidth
                            onChange={onChangeTextData} />
                    </FieldBox>
                    </form>
                </InputColumn>

                <Box style={{padding: 10}}>
                    <ul>
                    <li>암호 변경이 완료되면 자동 로그아웃되며 다시 로그인해야 합니다.</li>
                    <li>메일주소는 향후 암호리셋 등에 사용될 수 있으므로 입력을 권장합니다.</li>
                    <li>메일주소는 반드시 개인용이어야 합니다.</li>
                    </ul>

                    <Button
                        startIcon={<CloudUploadIcon />}
                        onClick={onClickSubmit}
                        fullWidth
                        disabled={hasError || !changed || inputData.newPassword !== inputData.confirm}
                        variant={AppPalette.VariantContained} color={AppPalette.PrimaryColor}
                        style={{marginTop: 10}}
                    >
                        변경
                    </Button>
                </Box>

            </EditorContentRow>
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </InputContainer>
    );

}